import { FieldModel, ResourceDetail, ResourceDetailsUpdate } from '@cibo/core'
import { fieldNameColumn, useFields, useUpdateMultipleFields } from '@cibo/landmanager'
import { DataGridPro, Markdown, ResourceDetailFeatureTaskEditorProps } from '@cibo/ui'
import { Button, ButtonGroup, Collapse, Stack, Typography } from '@mui/material'
import {
  GridRowHeightReturnValue,
  GridRowId,
  GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { assocPath, pathEq, propEq } from 'ramda'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const BULK_ANSWERS = ['yes', 'no', 'unknown'] as const
export type BulkAnswer = typeof BULK_ANSWERS[number]

export type BooleanRollupProps<T extends ResourceDetail = ResourceDetail> = {
  detailKey?: keyof T['input'] | keyof T['result']
  ns: string
  answerValues?: Partial<Record<BulkAnswer, { value: any }>>
  rowSelectedIndicates?: 'yes' | 'no'
  showTableAnswers?: BulkAnswer[]
} & ResourceDetailFeatureTaskEditorProps<T>

export const BooleanRollup = <T extends ResourceDetail = ResourceDetail>({
  answerValues = { yes: { value: 'yes' }, no: { value: 'no' } },
  rowSelectedIndicates = 'yes',
  detailRequirements: [{ traitId, year, resultsOnly }],
  ns,
  detailKey,
  onError,
  onSuccess,
  onUpdating,
  resourceIds,
  userRole,
  ownerName,
  showTableAnswers = ['yes'],
}: BooleanRollupProps<T>) => {
  const { t } = useTranslation('@cibo/landmanager/BooleanRollup')
  const updateFields = useUpdateMultipleFields()
  const fieldModels = useFields(resourceIds)
  const [answer, setAnswer] = useState<BulkAnswer>()

  const [ready, setReady] = useState<boolean>(false)
  const [animate, setAnimate] = useState<boolean>(false)
  const tableApiRef = useGridApiRef()
  const columns = useMemo(() => {
    return [fieldNameColumn({ t })]
  }, [])

  const rowDeselectedIndicates: BulkAnswer = rowSelectedIndicates === 'yes' ? 'no' : 'yes'

  const valuePath = useMemo(() => {
    const valuePath: Array<string> = [resultsOnly ? 'result' : 'input']
    if (detailKey) valuePath.push(detailKey as string)
    return valuePath
  }, [detailKey, resultsOnly])

  const detailHasAnswer = useCallback(
    (answer: BulkAnswer) => pathEq(valuePath, answerValues[answer]?.value),
    [valuePath, answerValues]
  )

  useEffect(() => {
    if (!ready && !!fieldModels.dataUpdatedAt) {
      const details = fieldModels.data?.map(({ resolveStandingDetail, resourceId }) => ({
        resourceId,
        detail: resolveStandingDetail(traitId, year),
      }))
      const existingDetails = details?.filter(({ detail }) => !!detail)

      const selectionModel =
        existingDetails
          ?.filter(({ detail }) => detailHasAnswer(rowSelectedIndicates)(detail))
          .map(({ resourceId }) => resourceId) ?? []

      tableApiRef.current?.selectRows(selectionModel)

      if (existingDetails?.length === details?.length) {
        // all fields have details
        if (selectionModel.length > 0) {
          // at least one indicates its row should be selected
          setAnswer(rowSelectedIndicates)
        } else {
          // none selected
          setAnswer(rowDeselectedIndicates)
        }
      } else {
        // one or more fields have no detail set
        if (existingDetails?.length === 0) {
          // none are set
          setAnswer(undefined)
        } else {
          // at least one is set
          setAnswer(rowSelectedIndicates)
        }
      }

      setReady(true)
    }
  }, [fieldModels.dataUpdatedAt, tableApiRef.current])

  const isRowSelectable = ({ id }: GridRowParams<FieldModel>) => {
    return !fieldModels.data
      ?.find(({ resourceId }) => resourceId === id)
      ?.details.some(detail => detail.traitId === traitId && detail.immutable)
  }

  const syncAnswers = (answers: Array<{ resourceId: string; answer: BulkAnswer }>) => {
    if (!ready) return

    const template = { traitId, year }

    const updates: ResourceDetailsUpdate[] = []

    answers.forEach(({ resourceId, answer }) => {
      const fieldModel = fieldModels.data?.find(propEq('resourceId', resourceId))
      if (!fieldModel) return

      const detail = fieldModel?.resolveStandingDetail(traitId, year)

      if (detail?.immutable) return

      if (!detailHasAnswer(answer)(detail)) {
        updates.push({
          resourceId: fieldModel.resourceId,
          details: [assocPath(valuePath, answerValues?.[answer]?.value, template)],
        })
      }
    })

    if (updates.length === 0) return

    onUpdating?.()
    updateFields
      .mutateAsync(updates)
      .then(() => {
        fieldModels.refetch().then(onSuccess)
      })
      .catch(onError)
  }

  const handleClickBulkAnswer = (newAnswer: BulkAnswer) => () => {
    setAnimate(true)

    if (answer === newAnswer) return

    setAnswer(newAnswer)

    if (newAnswer !== 'unknown') {
      tableApiRef.current?.selectRows(
        tableApiRef.current.getAllRowIds(),
        newAnswer === rowSelectedIndicates,
        true
      )
    } else {
      const answers = fieldModels.data?.map(({ resourceId }) => ({
        resourceId,
        answer: newAnswer,
      }))
      answers && syncAnswers(answers)
    }
  }

  const onSelectionModelChange = (selectionModel: GridRowId[]) => {
    if (!ready) return

    const answers: Array<{ resourceId: string; answer: BulkAnswer }> = []

    fieldModels.data?.forEach(fieldModel => {
      const detail = fieldModel.resolveStandingDetail(traitId, year)

      if (detail?.immutable) return

      answers.push({
        resourceId: fieldModel.resourceId,
        answer: selectionModel.includes(fieldModel.resourceId)
          ? rowSelectedIndicates
          : rowDeselectedIndicates,
      })
    })

    syncAnswers(answers)
  }

  const getRowHeight = useCallback((): GridRowHeightReturnValue => 'auto', [])
  const allImmutable = useMemo(() => {
    return fieldModels.data?.every(({ details }) =>
      details.some(detail => detail.traitId === traitId && detail.immutable)
    )
  }, [fieldModels?.dataUpdatedAt])

  return (
    <Stack spacing={3} flex={1}>
      <Markdown
        overrides={{
          em: {
            component: ({ children }: { children: ReactNode }) => (
              <Typography variant="body2" color="text.secondary" component="span">
                {children}
              </Typography>
            ),
          },
        }}
      >
        {t('question', { context: userRole, name: ownerName, ns, count: fieldModels.data?.length })}
      </Markdown>

      <Stack>
        <ButtonGroup color="secondary" disabled={allImmutable || !ready}>
          {answerValues['yes'] && (
            <Button
              data-testid="boolean-rollup-yes"
              onClick={handleClickBulkAnswer('yes')}
              variant={answer === 'yes' ? 'contained' : 'outlined'}
              disabled={updateFields.isLoading}
              disableElevation
            >
              {t('yes')}
            </Button>
          )}
          {answerValues['no'] && (
            <Button
              data-testid="boolean-rollup-no"
              disabled={updateFields.isLoading}
              onClick={handleClickBulkAnswer('no')}
              variant={answer === 'no' ? 'contained' : 'outlined'}
              disableElevation
            >
              {t('no')}
            </Button>
          )}
          {answerValues['unknown'] && (
            <Button
              data-testid="boolean-rollup-unknown"
              disabled={updateFields.isLoading}
              onClick={handleClickBulkAnswer('unknown')}
              variant={answer === 'unknown' ? 'contained' : 'outlined'}
              disableElevation
            >
              {t('unknown')}
            </Button>
          )}
        </ButtonGroup>

        <Collapse
          in={answer && showTableAnswers.includes(answer) && (fieldModels.data?.length ?? 0) > 1}
          timeout={animate ? undefined : 0}
        >
          <Stack mt={3} spacing={3}>
            <Markdown>{t('tableCta', { context: userRole, name: ownerName, ns })}</Markdown>
            <DataGridPro
              apiRef={tableApiRef}
              onSelectionModelChange={onSelectionModelChange}
              isRowSelectable={isRowSelectable}
              rows={fieldModels.data ?? []}
              getRowHeight={getRowHeight}
              columns={columns}
              loading={updateFields.isLoading}
              autoHeight
              checkboxSelection
              hideCellFocus
            />
          </Stack>
        </Collapse>
      </Stack>
    </Stack>
  )
}
